import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { BarChart3 } from "lucide-react";

const sel = "min-h-[44px] rounded-lg border border-slate-200 bg-white px-2 text-sm";
const pct = (x) => `${Math.round(x * 100)}%`;

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900" data-testid={testid}>{value}</div>
    </div>
  );
}

function LetterInsight({ l }) {
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 text-sm" data-testid={`insight-row-${l.letter_key}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold text-slate-900">{l.name}</span>
        <span className="text-xs text-slate-500">{l.round === "special" ? "Special request" : `Round ${l.round}`} · {l.live ? "Live" : "Disabled"}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-slate-700">
        <span>Generated: <b data-testid={`insight-generated-${l.letter_key}`}>{l.generated}</b></span>
        <span>Members: <b>{l.members}</b></span>
        <span>Mailed: <b>{l.mailed}</b></span>
        <span>Regenerated / edited: <b>{l.regenerated} / {l.edited}</b></span>
        <span>Blocked attempts: <b data-testid={`insight-blocked-${l.letter_key}`}>{l.blocked_attempts}</b></span>
        <span>Shown as blocked: <b>{l.blocked_views} of {l.shown}</b>{l.shown ? ` (${pct(l.blocked_view_rate)})` : ""}</span>
        <span>Standard wording used: <b>{l.generated ? pct(l.fallback_rate) : "—"}</b></span>
      </div>
      {l.block_reasons.length > 0 && (
        <ul className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2 space-y-0.5" data-testid={`insight-reasons-${l.letter_key}`}>
          {l.block_reasons.map((r) => <li key={r.reason}>{r.count}× {r.reason}</li>)}
        </ul>
      )}
    </li>
  );
}

export function LetterInsights() {
  const [days, setDays] = useState(30);
  const [sort, setSort] = useState("used");
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/letter-library/insights", { params: { days } }).then((r) => setData(r.data)).catch(() => setData(null)); }, [days]);
  const letters = data ? [...data.letters].sort((a, b) => (sort === "used" ? b.generated - a.generated : (b.blocked_attempts + b.blocked_views) - (a.blocked_attempts + a.blocked_views))) : [];
  return (
    <section className="mt-6" data-testid="letter-insights">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><BarChart3 size={18} className="text-purple-600" />Letter usage insights</h2>
        <div className="flex gap-2">
          <select className={sel} value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="insights-days">
            {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
          <select className={sel} value={sort} onChange={(e) => setSort(e.target.value)} data-testid="insights-sort">
            <option value="used">Most used</option><option value="blocked">Most blocked</option>
          </select>
        </div>
      </div>
      {!data ? <p className="mt-3 text-sm text-slate-500">Loading insights…</p> : (
        <>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Letters generated" value={data.totals.generated} testid="insights-total-generated" />
            <Stat label="Letters mailed" value={data.totals.mailed} testid="insights-total-mailed" />
            <Stat label="Blocked attempts" value={data.totals.blocked_attempts} testid="insights-total-blocked" />
            <Stat label="Account views" value={data.totals.library_views} testid="insights-total-views" />
          </div>
          <p className="mt-2 text-xs text-slate-500">“Shown as blocked” counts how often a letter appeared as Requires Response, Requires Evidence, Not Applicable or Admin Disabled for a member's account. Blocked attempts are generation requests refused by eligibility or fact checks.</p>
          <ul className="mt-3 grid gap-2">{letters.map((l) => <LetterInsight key={l.letter_key} l={l} />)}</ul>
        </>
      )}
    </section>
  );
}
